"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import routinesData from "@/lib/content/routines.json";
import { products as catalog } from "@/lib/content/products";

import { isRealPhoto } from "@/lib/brand-mapping";

function realImageForSku(sku: string): string | undefined {
  const p = catalog.find((x) => x.sku === sku);
  return p?.image && isRealPhoto(p.image) ? p.image : undefined;
}

interface RoutineBuilderProps {
  locale: string;
  dict: Record<string, string>;
}

const STEP_META = [
  { key: "cleanse", num: 1, label: "Cleanse" },
  { key: "tone", num: 2, label: "Tone & Balance" },
  { key: "treat", num: 3, label: "Focus & Treat" },
  { key: "hydrate", num: 4, label: "Boost & Hydrate" },
  { key: "protect", num: 5, label: "Protect" },
] as const;

const AGE_GROUPS = ["20+", "30+", "40+", "50+"] as const;

const NAME_TO_SKU: Record<string, string> = {
  "Bright Glow": "1002",
  "Bright Glow Cleansing Foam": "1002",
  "Bright Glow Cleansing Face Foam": "1002",
  "Clear Deep": "1003",
  "Clear Deep Cleansing Face Foam": "1003",
  "Fresh Calm": "1001",
  "Fresh Calm Cleansing Gel": "1001",
  "80% GFF & Seaweed Tone Essence": "1006",
  "10% GFF & 1% BHA Balance Mist": "1007",
  "10% GFF & BHA Balance Mist": "1007",
  "5x HA & Ceramide Hydra Intensive Serum": "1032",
  "15% Vitamin C + HA Antioxidizing Defense Serum": "1030",
  "15% Vitamin C + HA Collagen Boost Cream": "1031",
  "3% Amino Acids Nourish & Hydrate Cream": "1033",
  "3% Amino Acids Comfort Hydration Gel": "1032",
  "0.1% Retinol Glow Skin Milk": "1020",
  "0.3% Retinol + Bakuchiol Advanced Glow Milk": "1021",
  "3% Peptides + 1% Oligo HA Lifting Serum": "1023",
  "Sun Protection Cream Face SPF 50+": "1082",
  "Matte Shield SPF 50+": "1082",
  "5% Vitamin C Blemish Relief Serum": "1015",
  "2% Alpha-Arbutin + 2% Vitamin C Brightening Serum": "1040",
  "1% Alpha-Arbutin Brighten & Protect Cream": "1041",
  "1% Alpha-Arbutin Brighten & Protect Cream SPF30": "1041",
};

function getSkuForProduct(name: string): string | null {
  if (NAME_TO_SKU[name]) return NAME_TO_SKU[name];
  for (const [key, sku] of Object.entries(NAME_TO_SKU)) {
    if (
      name.toLowerCase().includes(key.toLowerCase()) ||
      key.toLowerCase().includes(name.toLowerCase())
    ) {
      return sku;
    }
  }
  return null;
}

function ProductThumbnail({ name, size = 56 }: { name: string; size?: number }) {
  const sku = getSkuForProduct(name);
  if (!sku) return null;
  const src = realImageForSku(sku);
  if (!src) {
    return (
      <div
        style={{
          width: size,
          height: size,
          background: "var(--bp-bg-2)",
          border: "1px solid var(--bp-rule)",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--bp-mono)",
          fontSize: 10,
          color: "var(--bp-ink-3)",
          letterSpacing: "0.08em",
        }}
      >
        {sku}
      </div>
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        background: "var(--bp-bg-2)",
        border: "1px solid var(--bp-rule)",
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Image src={src} alt={name} width={size} height={size} style={{ objectFit: "cover", width: "100%", height: "100%" }} />
    </div>
  );
}

function selectStyle(): React.CSSProperties {
  return {
    width: "100%",
    padding: "12px 14px",
    background: "var(--bp-bg)",
    border: "1px solid var(--bp-rule)",
    fontFamily: "var(--bp-mono)",
    fontSize: 12,
    letterSpacing: "0.08em",
    color: "var(--bp-ink)",
    outline: "none",
    appearance: "none",
    backgroundImage:
      "linear-gradient(45deg, transparent 50%, var(--bp-ink) 50%), linear-gradient(135deg, var(--bp-ink) 50%, transparent 50%)",
    backgroundPosition: "calc(100% - 18px) calc(50% - 2px), calc(100% - 13px) calc(50% - 2px)",
    backgroundSize: "5px 5px, 5px 5px",
    backgroundRepeat: "no-repeat",
    paddingRight: 36,
    cursor: "pointer",
  };
}

export default function RoutineBuilder({ locale, dict }: RoutineBuilderProps) {
  const [skinTypeIdx, setSkinTypeIdx] = useState(0);
  const [ageGroup, setAgeGroup] = useState<string>("20+");
  const [timeOfDay, setTimeOfDay] = useState<"am" | "pm">("am");
  const [savedFlash, setSavedFlash] = useState(false);
  const [copiedFlash, setCopiedFlash] = useState(false);
  const [isSavedRoutine, setIsSavedRoutine] = useState(false);

  const skinType = routinesData.skinTypes[skinTypeIdx];
  const variation = skinType?.ageVariations?.[ageGroup as keyof typeof skinType.ageVariations];
  const routine = variation?.[timeOfDay as keyof typeof variation] as
    | Record<string, unknown>
    | undefined;

  const routineId = `st${skinTypeIdx}-${ageGroup.replace("+", "p")}-${timeOfDay}`;

  const getProducts = (key: string): string[] => {
    if (!routine) return [];
    const plural = routine[key + "s"];
    const singular = routine[key];
    if (Array.isArray(plural)) return plural as string[];
    if (Array.isArray(singular)) return singular as string[];
    if (typeof plural === "string") return [plural];
    if (typeof singular === "string") return [singular];
    return [];
  };

  const buildSteps = useCallback(() => {
    if (!skinType || !routine) return [];
    return [
      { ...STEP_META[0], products: [skinType.cleanser] },
      { ...STEP_META[1], products: [skinType.toner] },
      { ...STEP_META[2], products: getProducts("serum") },
      {
        ...STEP_META[3],
        products: [...getProducts("booster"), ...getProducts("moisturizer")],
      },
      { ...STEP_META[4], products: getProducts("spf") },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skinTypeIdx, ageGroup, timeOfDay]);

  const steps = buildSteps();
  const allProducts = steps.flatMap((s) => s.products).filter(Boolean);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rid = params.get("routine");
    if (!rid) return;
    const match = rid.match(/^st(\d+)-(\d+p)-([ap]m)$/);
    if (!match) return;
    const idx = parseInt(match[1]);
    const age = match[2].replace("p", "+");
    const tod = match[3] as "am" | "pm";
    if (idx >= 0 && idx < routinesData.skinTypes.length) {
      setSkinTypeIdx(idx);
      setAgeGroup(age);
      setTimeOfDay(tod);
    }
  }, []);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("bpderma_saved_routines") || "[]") as string[];
      setIsSavedRoutine(saved.includes(routineId));
    } catch {
      setIsSavedRoutine(false);
    }
  }, [routineId]);

  function handleSave() {
    try {
      const existing = JSON.parse(
        localStorage.getItem("bpderma_saved_routines") || "[]"
      ) as string[];
      if (!existing.includes(routineId)) {
        existing.push(routineId);
        localStorage.setItem("bpderma_saved_routines", JSON.stringify(existing));
      }
      const meta = JSON.parse(
        localStorage.getItem("bpderma_routine_meta") || "{}"
      ) as Record<string, object>;
      meta[routineId] = {
        skinType: skinType.name,
        ageGroup,
        timeOfDay,
        savedAt: new Date().toISOString(),
        steps: steps.map((s) => ({ label: s.label, products: s.products })),
      };
      localStorage.setItem("bpderma_routine_meta", JSON.stringify(meta));
      setSavedFlash(true);
      setIsSavedRoutine(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch {
      /* localStorage not available */
    }
  }

  function handleShare() {
    const url = new URL(window.location.href);
    url.searchParams.set("routine", routineId);
    navigator.clipboard
      .writeText(url.toString())
      .then(() => {
        setCopiedFlash(true);
        setTimeout(() => setCopiedFlash(false), 2500);
      })
      .catch(() => {
        window.prompt("Copy this link:", url.toString());
      });
  }

  return (
    <div>
      {/* Controls */}
      <div
        className="bp-rb-controls"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 16,
          marginBottom: 32,
        }}
      >
        <ControlField label={dict.skinType ?? "Skin Type"}>
          <select
            value={skinTypeIdx}
            onChange={(e) => setSkinTypeIdx(Number(e.target.value))}
            style={selectStyle()}
          >
            {routinesData.skinTypes.map((st, i) => (
              <option key={i} value={i}>
                {st.name}
              </option>
            ))}
          </select>
        </ControlField>

        <ControlField label={dict.ageGroup ?? "Age Group"}>
          <select
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            style={selectStyle()}
          >
            {AGE_GROUPS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </ControlField>

        <ControlField label={dict.timeOfDay ?? "Time of Day"}>
          <div style={{ display: "flex", border: "1px solid var(--bp-ink)" }}>
            {(["am", "pm"] as const).map((t) => {
              const active = timeOfDay === t;
              return (
                <button
                  key={t}
                  onClick={() => setTimeOfDay(t)}
                  className="bp-mono"
                  style={{
                    flex: 1,
                    padding: "12px 0",
                    fontFamily: "var(--bp-mono)",
                    fontSize: 12,
                    letterSpacing: "0.16em",
                    background: active ? "var(--bp-ink)" : "transparent",
                    color: active ? "var(--bp-bg)" : "var(--bp-ink)",
                    border: 0,
                    cursor: "pointer",
                  }}
                >
                  {t.toUpperCase()}
                </button>
              );
            })}
          </div>
        </ControlField>
      </div>

      {/* Routine panel */}
      <div
        style={{
          background: "var(--bp-bg)",
          border: "1px solid var(--bp-rule)",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--bp-rule)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <span className="bp-mono-sm">{dict.yourRoutine ?? "YOUR ROUTINE"}</span>
            <div
              style={{
                fontFamily: "var(--bp-serif)",
                fontSize: 26,
                fontWeight: 300,
                letterSpacing: "-0.01em",
                marginTop: 4,
              }}
            >
              {skinType.name}
            </div>
            <div className="bp-mono-sm" style={{ marginTop: 4 }}>
              {ageGroup} · {timeOfDay === "am" ? "MORNING" : "EVENING"}
            </div>
          </div>
          <span className="bp-mono-sm">
            {String(allProducts.length).padStart(2, "0")} PRODUCTS · {steps.length} STEPS
          </span>
        </div>

        <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {steps.map((step, idx) => (
            <li
              key={step.key}
              style={{
                display: "grid",
                gridTemplateColumns: "60px 160px 1fr",
                gap: 20,
                padding: "22px 24px",
                borderBottom: idx < steps.length - 1 ? "1px solid var(--bp-rule)" : "none",
                alignItems: "start",
              }}
              className="bp-rb-step"
            >
              <span className="bp-mono" style={{ color: "var(--bp-ink)" }}>
                {String(step.num).padStart(2, "0")}
              </span>
              <div>
                <div
                  style={{
                    fontFamily: "var(--bp-serif)",
                    fontSize: 18,
                    fontWeight: 300,
                    letterSpacing: "-0.01em",
                    color: "var(--bp-ink)",
                  }}
                >
                  {step.label}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
                {step.products.length > 0 ? (
                  step.products.map((p, i) => (
                    <div
                      key={i}
                      style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}
                    >
                      <ProductThumbnail name={p} size={56} />
                      <span
                        style={{
                          fontSize: 14,
                          color: "var(--bp-ink)",
                          lineHeight: 1.35,
                        }}
                      >
                        {p}
                      </span>
                    </div>
                  ))
                ) : (
                  <span className="bp-mono-sm" style={{ color: "var(--bp-ink-3)" }}>
                    — NOT INCLUDED IN THIS ROUTINE
                  </span>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, paddingTop: 4 }}>
        <button
          onClick={handleSave}
          disabled={isSavedRoutine}
          className="bp-mono-sm"
          style={{
            padding: "12px 16px",
            background: savedFlash || isSavedRoutine ? "var(--bp-ink)" : "transparent",
            color: savedFlash || isSavedRoutine ? "var(--bp-bg)" : "var(--bp-ink)",
            border: "1px solid var(--bp-ink)",
            cursor: isSavedRoutine ? "default" : "pointer",
            opacity: isSavedRoutine && !savedFlash ? 0.7 : 1,
          }}
        >
          {savedFlash ? "✓ SAVED" : isSavedRoutine ? "✓ SAVED" : "SAVE ROUTINE"}
        </button>

        <button
          onClick={handleShare}
          className="bp-mono-sm"
          style={{
            padding: "12px 16px",
            background: copiedFlash ? "var(--bp-ink)" : "transparent",
            color: copiedFlash ? "var(--bp-bg)" : "var(--bp-ink)",
            border: "1px solid var(--bp-ink)",
            cursor: "pointer",
          }}
        >
          {copiedFlash ? "✓ LINK COPIED" : "SHARE ROUTINE ↗"}
        </button>

        <a
          href={`/${locale}/products`}
          className="bp-btn"
          style={{ padding: "12px 18px" }}
        >
          SHOP PRODUCTS →
        </a>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .bp-rb-controls { grid-template-columns: 1fr !important; }
          .bp-rb-step { grid-template-columns: 40px 1fr !important; }
          .bp-rb-step > :nth-child(2) { display: none !important; }
        }
        select:focus-visible { outline: 1px solid var(--bp-ink); outline-offset: 2px; }
      `}</style>
    </div>
  );
}

function ControlField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="bp-mono-sm" style={{ marginBottom: 8 }}>
        {label.toUpperCase()}
      </div>
      {children}
    </div>
  );
}
